import { useCallback, useEffect, useRef, useState } from 'react'

import { SmepEvent, SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CLEARED_TIME, isClearedTime } from 'domains/Workspaces/Events/schedule'
import { useEventUpdateMutation } from 'hooks/mutations/smep'
import { eventQueryKeys } from 'services/queryKeys'

import { newId } from './ids'
import { parseConfig, serializeConfig } from './model'
import type { ConfigFormValues } from './types'

export type UseConfigFormProps = {
  event: SmepEvent
  config: SmepEventConfig
  /** Called after the config was successfully persisted (e.g. to collapse an inline editor). */
  onSaved?: () => void
  /** Also edit + persist the event schedule (`manualStartTime`/`manualEndTime`) alongside the config. */
  includeSchedule?: boolean
  /** Also edit + persist who is on call (`onCallUserIds`) alongside the config. */
  includeOnCall?: boolean
}

const scheduleDefaults = (event: SmepEvent): Pick<ConfigFormValues, 'manualStartTime' | 'manualEndTime'> => ({
  manualStartTime: isClearedTime(event.startTime) ? '' : event.startTime,
  manualEndTime: isClearedTime(event.endTime) ? null : event.endTime,
})

export const useConfigForm = ({ event, config, onSaved, includeSchedule, includeOnCall }: UseConfigFormProps) => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const eventId = event.id

  /**
   * Everything the form holds that is not the config: the schedule and the on call list. Kept in one
   * place because all three reset paths below - adopting server truth, reloading, and settling after
   * a save - have to restore them together or the form silently loses them.
   */
  const eventDefaults = useCallback(
    (source: SmepEvent) => ({
      ...(includeSchedule ? scheduleDefaults(source) : {}),
      ...(includeOnCall ? { onCall: source.onCall } : {}),
    }),
    [includeSchedule, includeOnCall],
  )

  const formMethods = useForm<ConfigFormValues>({
    defaultValues: { ...parseConfig(config), ...eventDefaults(event) },
  })
  const { mutateAsync, isPending } = useEventUpdateMutation()

  const [externalChange, setExternalChange] = useState(false)
  const lastConfigRef = useRef(config)

  // Adopt server truth when the underlying config changes (e.g. saved from the
  // JSON tab). Never silently discard in-progress edits — surface a banner instead.
  useEffect(() => {
    const next = event.config
    if (!next || next === lastConfigRef.current) {
      return
    }
    lastConfigRef.current = next
    if (formMethods.formState.isDirty) {
      setExternalChange(true)
    } else {
      formMethods.reset({ ...parseConfig(next), ...eventDefaults(event) })
    }
  }, [event, event.config, formMethods, eventDefaults])

  const reloadFromServer = useCallback(() => {
    if (event.config) {
      formMethods.reset({ ...parseConfig(event.config), ...eventDefaults(event) })
    }
    setExternalChange(false)
  }, [event, formMethods, eventDefaults])

  const handleSubmit = formMethods.handleSubmit(async (values) => {
    // Merge into the freshest server config so a concurrent JSON-tab change to an
    // unsupported field is preserved (no clobber).
    const fresh = await queryClient.ensureQueryData(eventQueryKeys.detail({ eventId }))
    const payload = serializeConfig(fresh.config ?? config, values, newId)

    const updated = await mutateAsync({
      eventId,
      smepEventEdit: {
        config: payload,
        ...(includeSchedule && {
          manualStartTime: values.manualStartTime || CLEARED_TIME,
          manualEndTime: values.manualEndTime ?? CLEARED_TIME,
        }),
        ...(includeOnCall && { onCallUserIds: (values.onCall ?? []).map((person) => person.userId) }),
      },
    })

    if (updated.config) {
      lastConfigRef.current = updated.config
      /*
       * Reset from the whole saved event, not just its config, so the schedule and the on call list
       * survive the save instead of becoming undefined. The patch response returns on call people
       * unresolved - only `GET /events/{eventId}` fills in their contact details - so the names here
       * come back on the detail refetch this mutation triggers, adopted by the effect above.
       */
      formMethods.reset({ ...parseConfig(updated.config), ...eventDefaults(updated) }, { keepSubmitCount: true })
    }
    setExternalChange(false)
    enqueueSnackbar(t('domain:Workspace.Event.ConfigEditor.saved', { name: updated.name }), { variant: 'success' })
    onSaved?.()
  })

  return { formMethods, handleSubmit, isSaving: isPending, externalChange, reloadFromServer }
}
